import React, { Suspense } from "react";
import { PlatformModeProvider } from "./context/PlatformModeContext";
import { Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Portfolio from "./pages/Portfolio";
import PortfolioDetail from "./pages/PortfolioDetail";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";
import Contact from "./pages/Contact";
import MarketingLayout from "./layouts/MarketingLayout";
import ClientPortalLayout from "./layouts/ClientPortalLayout";
import ClientRESavedLegacyRedirect from "./layouts/ClientRESavedLegacyRedirect";
import ClientMyPropertyLegacyRedirect from "./layouts/ClientMyPropertyLegacyRedirect";
import ClientMarketSnapshotLegacyRedirect from "./layouts/ClientMarketSnapshotLegacyRedirect";
import ClientAccountLayout from "./layouts/ClientAccountLayout";
import ClientRealEstateLayout from "./layouts/ClientRealEstateLayout";
import ClientResourcesLayout from "./layouts/ClientResourcesLayout";
import ClientPortalLegacyRedirect from "./layouts/ClientPortalLegacyRedirect";

import AdminLayout from "./pages/AdminLayout";
import TradeLayout from "./pages/TradeLayout";

import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { connectFirebaseEmulatorsIfEnabled } from "./firebaseEmulatorConnect";
import { bindDesignBoardReadCacheAuth } from "./designBoardReadCacheAuthBinding";

// Client portal pages (lazy)
const ClientPortal = React.lazy(() => import("./pages/ClientPortal"));
const AuthEmailActionHandler = React.lazy(() => import("./pages/AuthEmailActionHandler"));
const ClientProjectDetail = React.lazy(() => import("./pages/ClientProjectDetail"));
const ClientFileMarkup = React.lazy(() => import("./pages/ClientFileMarkup"));
const ClientProfile = React.lazy(() => import("./pages/ClientProfile"));
const ClientRealEstateEntry = React.lazy(() => import("./pages/ClientRealEstateEntry"));
const ClientREOnboarding = React.lazy(() => import("./pages/ClientREOnboarding"));
const ClientRESearchLayout = React.lazy(() => import("./pages/ClientRESearch"));
const ClientRESearchHub = React.lazy(() =>
  import("./pages/ClientRESearch").then((m) => ({ default: m.ClientRESearchHub }))
);
const ClientRESearchHouses = React.lazy(() =>
  import("./pages/ClientRESearch").then((m) => ({ default: m.ClientRESearchHouses }))
);
const ClientRESearchNeighbourhoods = React.lazy(() =>
  import("./pages/ClientRESearch").then((m) => ({ default: m.ClientRESearchNeighbourhoods }))
);
const ClientRENeighbourhoodProfile = React.lazy(() =>
  import("./pages/ClientRESearch").then((m) => ({ default: m.ClientRENeighbourhoodProfile }))
);
const ClientRESearchSaved = React.lazy(() =>
  import("./pages/ClientRESearch").then((m) => ({ default: m.ClientRESearchSaved }))
);
const ClientRECondos = React.lazy(() => import("./pages/ClientRECondos"));
const ClientRETransactions = React.lazy(() => import("./pages/ClientRETransactions"));
const ClientREMyProperty = React.lazy(() => import("./pages/ClientREMyProperty"));
const ClientREMyProperties = React.lazy(() => import("./pages/ClientREMyProperties"));
const ClientREPropertyWorkspace = React.lazy(() => import("./pages/ClientREPropertyWorkspace"));
const ClientRECommunityRollupProfile = React.lazy(() => import("./pages/ClientRECommunityRollupProfile"));
const ClientRESalePrep = React.lazy(() => import("./pages/ClientRESalePrep"));
const ClientResourcesOverview = React.lazy(() => import("./pages/ClientResourcesOverview"));
const ClientResourcesCalculators = React.lazy(() => import("./pages/ClientResourcesCalculators"));
const ClientResourcesLinks = React.lazy(() => import("./pages/ClientResourcesLinks"));
const StaffCompaniesDirectoryPage = React.lazy(() => import("./pages/StaffCompaniesDirectoryPage"));

// Trade portal pages (lazy)
const TradeHome = React.lazy(() => import("./pages/TradeHome"));

// Admin pages (lazy)
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const AdminClients = React.lazy(() => import("./pages/AdminClients"));
const AdminRFIs = React.lazy(() => import("./pages/AdminRFIs"));
const AdminTasks = React.lazy(() => import("./pages/AdminTasks"));
const AdminProjects = React.lazy(() => import("./pages/AdminProjects"));
const AdminProjectDetail = React.lazy(() => import("./pages/AdminProjectDetail"));
const AdminBulkFileCollaborators = React.lazy(() => import("./pages/AdminBulkFileCollaborators"));
const AdminSendReviewSummaryEmail = React.lazy(() => import("./pages/AdminSendReviewSummaryEmail"));
const AdminJurisdictions = React.lazy(() => import("./pages/AdminJurisdictions"));
const AdminSettings = React.lazy(() => import("./pages/AdminSettings"));
const UserDetail = React.lazy(() => import("./pages/UserDetail"));
const AdminPublicProjects = React.lazy(() => import("./pages/AdminPublicProjects"));
const AdminPortfolio = React.lazy(() => import("./pages/AdminPortfolio"));
const AdminDataHealth = React.lazy(() => import("./pages/AdminDataHealth"));
const AdminREHome = React.lazy(() => import("./pages/AdminREHome"));
const AdminREListings = React.lazy(() => import("./pages/AdminREListings"));
const AdminRETransactions = React.lazy(() => import("./pages/AdminRETransactions"));
const AdminREClients = React.lazy(() => import("./pages/AdminREClients"));
const AdminREDocuments = React.lazy(() => import("./pages/AdminREDocuments"));
const AdminREReports = React.lazy(() => import("./pages/AdminREReports"));
const AdminRECondos = React.lazy(() => import("./pages/AdminRECondos"));
const AdminRECondoClientAccess = React.lazy(() => import("./pages/AdminRECondoClientAccess"));
const AdminCMARequests = React.lazy(() => import("./pages/AdminCMARequests"));
const AdminCmaWorkbench = React.lazy(() => import("./pages/AdminCmaWorkbench"));
const AdminREPropertyWorkspace = React.lazy(() => import("./pages/AdminREPropertyWorkspace"));
const AdminClientPropertiesHub = React.lazy(() => import("./pages/AdminClientPropertiesHub"));
const AdminNotificationOutbox = React.lazy(() => import("./pages/AdminNotificationOutbox"));
const AdminPasswordResetAudit = React.lazy(() => import("./pages/AdminPasswordResetAudit"));
const AdminREClientIntent = React.lazy(() => import("./pages/AdminREClientIntent"));

const firebaseConfig = {
  apiKey: "AIzaSyCz3yjCzs_6WLYwjCUkDDChUAxnKn1SUG0",
  authDomain: "markbeattie-website.firebaseapp.com",
  projectId: "markbeattie-website",
  storageBucket: "markbeattie-website.firebasestorage.app",
  messagingSenderId: "453651893893",
  appId: "1:453651893893:web:48c12c94b6e1ca496d23ec",
};

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

bindDesignBoardReadCacheAuth(auth);
connectFirebaseEmulatorsIfEnabled(app, { auth, db, storage });

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center p-8 text-slate-600" role="status" aria-live="polite">
      Loading…
    </div>
  );
}

/** CL-310D: legacy /trade/projects/* → canonical client portal URL (query preserved). */
function TradeProjectToClientPortalRedirect() {
  const { projectId } = useParams();
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();
  const suffix = qs ? `?${qs}` : "";
  return (
    <Navigate
      to={`/client-portal/projects/${encodeURIComponent(projectId)}${suffix}`}
      replace
    />
  );
}

function App() {
  return (
    <PlatformModeProvider>
      <div className="min-h-screen flex flex-col">
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path="/auth/email-action" element={<AuthEmailActionHandler />} />

            {/* Client portal: dedicated shell (no marketing nav/footer) */}
            <Route path="/client-portal" element={<ClientPortalLayout />}>
              <Route index element={<ClientPortal />} />

              <Route path="account" element={<ClientAccountLayout />}>
                <Route index element={<Navigate to="profile" replace />} />
                <Route path="profile" element={<ClientProfile />} />
                <Route path="my-property" element={<ClientMyPropertyLegacyRedirect />} />
                <Route path="market-snapshot" element={<ClientMarketSnapshotLegacyRedirect />} />
              </Route>
              <Route
                path="profile"
                element={<ClientPortalLegacyRedirect to="/client-portal/account/profile" />}
              />
              <Route path="my-property" element={<ClientMyPropertyLegacyRedirect />} />
              <Route path="market-snapshot" element={<ClientMarketSnapshotLegacyRedirect />} />

              <Route path="real-estate" element={<ClientRealEstateLayout />}>
                <Route index element={<ClientRealEstateEntry />} />
                <Route path="onboarding" element={<ClientREOnboarding />} />
                <Route path="search" element={<ClientRESearchLayout />}>
                  <Route index element={<ClientRESearchHub />} />
                  <Route path="houses" element={<ClientRESearchHouses />} />
                  <Route path="neighbourhoods" element={<ClientRESearchNeighbourhoods />} />
                  <Route path="neighbourhoods/:neighbourhoodId" element={<ClientRENeighbourhoodProfile />} />
                  <Route
                    path="neighbourhoods/community/:commCode"
                    element={
                      <ClientRECommunityRollupProfile
                        hubPath="/client-portal/real-estate/search/neighbourhoods"
                      />
                    }
                  />
                  <Route path="saved" element={<ClientRESearchSaved />} />
                </Route>
                <Route path="condos" element={<ClientRECondos />} />
                <Route path="properties" element={<ClientREMyProperties />} />
                <Route path="properties/:propertyId" element={<ClientREPropertyWorkspace />} />
                <Route path="my-property" element={<ClientREMyProperty />} />
                <Route path="sale-prep" element={<ClientRESalePrep />} />
                <Route path="transactions" element={<ClientRETransactions />} />
                <Route path="saved" element={<ClientRESavedLegacyRedirect />} />
              </Route>

              <Route path="resources" element={<ClientResourcesLayout />}>
                <Route index element={<ClientResourcesOverview />} />
                <Route path="calculators" element={<ClientResourcesCalculators />} />
                <Route path="links" element={<ClientResourcesLinks />} />
              </Route>

              <Route path="companies-suppliers" element={<StaffCompaniesDirectoryPage />} />
              <Route path="projects/:projectId" element={<ClientProjectDetail />} />
              <Route path="markup/:projectId/:fileId" element={<ClientFileMarkup />} />
            </Route>

            {/* Trade portal: index only; project URLs redirect to client-portal (CL-310D). TradeProjectDetail retained for future use. */}
            <Route path="/trade/projects/:projectId" element={<TradeProjectToClientPortalRedirect />} />
            <Route path="/trade" element={<TradeLayout />}>
              <Route index element={<TradeHome />} />
            </Route>

            {/* Admin shell */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />

              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="rfis" element={<AdminRFIs />} />
              <Route path="tasks" element={<AdminTasks />} />
              <Route path="health" element={<AdminDataHealth />} />

              {/* Construction PM */}
              <Route path="projects" element={<AdminProjects />} />
              <Route path="projects/:projectId/rfis" element={<AdminRFIs />} />
              <Route path="projects/:projectId/file-collaborators" element={<AdminBulkFileCollaborators />} />
              <Route path="projects/:projectId/send-review-summary-email" element={<AdminSendReviewSummaryEmail />} />
              <Route path="projects/:projectId" element={<AdminProjectDetail />} />
              <Route path="jurisdictions" element={<AdminJurisdictions />} />
              <Route path="notifications" element={<AdminNotificationOutbox />} />
              <Route path="password-reset-audit" element={<AdminPasswordResetAudit />} />

              {/* Real Estate Services (separate mode) */}
              <Route path="real-estate" element={<AdminREHome />} />
              <Route path="real-estate/listings" element={<AdminREListings />} />
              <Route path="real-estate/transactions" element={<AdminRETransactions />} />
              <Route path="real-estate/clients" element={<AdminREClients />} />
              <Route path="real-estate/documents" element={<AdminREDocuments />} />
              <Route path="real-estate/reports" element={<AdminREReports />} />
              <Route path="real-estate/condos" element={<AdminRECondos />} />
              <Route path="real-estate/condo-client-access" element={<AdminRECondoClientAccess />} />
              <Route path="real-estate/cma-requests" element={<AdminCMARequests />} />
              <Route path="real-estate/client-properties" element={<AdminClientPropertiesHub />} />
              <Route path="real-estate/cma-workbench/:userId/:propertyId" element={<AdminCmaWorkbench />} />
              <Route path="real-estate/client-properties/:userId/:propertyId" element={<AdminREPropertyWorkspace />} />
              <Route path="real-estate/client-intent" element={<AdminREClientIntent />} />

              {/* Public editable content */}
              <Route path="public-projects" element={<AdminPublicProjects />} />
              <Route path="portfolio" element={<AdminPortfolio />} />

              <Route path="settings" element={<AdminSettings />} />
              <Route path="users/:userId" element={<UserDetail />} />
            </Route>

            {/* Public marketing site */}
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />

              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:slug" element={<ProjectDetail />} />

              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/portfolio/:slug" element={<PortfolioDetail />} />

              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/contact" element={<Contact />} />

              <Route
                path="*"
                element={<div className="container mx-auto p-4">404 - Page Not Found</div>}
              />
            </Route>
          </Routes>
        </Suspense>
      </div>
    </PlatformModeProvider>
  );
}

export default App;
